import * as Sentry from "@sentry/react";

import { useCallback } from "react";
import { captureReactException } from "@sentry/react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

import { Button } from "@/components/ui/button";
import { EmptyIcon } from "@/components/icons/empty";

import { cn } from "@/lib/utils";
import { handleLogout } from "@/lib/auth";
import { useAuthenticationStore } from "@/store/authentication";

interface ErrorLayoutProps extends React.HTMLAttributes<HTMLDivElement> {}

export function ErrorLayout({ children, className, ...props }: ErrorLayoutProps) {
  const user = useAuthenticationStore((store) => store.user);

  const renderFallback = useCallback(({ error }: FallbackProps) => {
    return (
      <div className={cn("flex flex-col items-center justify-center fixed inset-0", className)} {...props}>
        <div className="max-w-3xl px-4 text-center flex flex-col items-center">
          <EmptyIcon className="size-32" />
          <h1 className="text-base text-destructive mt-2">Oops! Something went wrong.</h1>
          <p className="text-sm text-muted-foreground mt-1">{error.message || "Unknown error"}. If this problem persists, send us a message.</p>
          <div className="mt-6 grid grid-cols-2 gap-2 w-full max-w-sm">
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              Logout
            </Button>
            <Button variant="outline" size="sm" className="col-span-2" onClick={() => (window.location.href = "/dashboard")}>
              Go to Dashboard
            </Button>
            <Button variant="outline" size="sm" className="col-span-2" onClick={() => Sentry.showReportDialog({ user: { name: user?.name || "", email: user?.email || "" } })}>
              Report the issue
            </Button>
          </div>
        </div>
      </div>
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ErrorBoundary onError={captureReactException} fallbackRender={renderFallback}>
      {children}
    </ErrorBoundary>
  );
}
